<template>
  <div class="tw-flex tw-gap-x-5">
    <filter-menu
      v-if="Settings.allowFiltering"
      :id="TABLE_ID"
      :show="filterMenuOpen"
      :tag-options="game.tag_options"
      :sort-options="LIBRARY_MODS_SORT_OPTIONS"
      @update="updateTable"
    />
    <div
      v-if="purchasedMods.data.length < 1 && !loadingPurchases"
      class="tw-flex-1"
    >
      <no-result
        class="tw-w-full"
        item-name="mod"
        :is-filtering="hasAnyFilters"
        @click="resetAllFilters"
      />
    </div>
    <div
      v-else
      :class="
        Settings.allowFiltering && filterMenuOpen
          ? 'md:tw-w-[calc(100%-17.25rem-1px)]'
          : 'tw-w-full'
      "
    >
      <modio-table
        ref="purchasedModsTable"
        :table-id="TABLE_ID"
        :row-schema="schema"
        :items="purchasedMods"
        :item-total="purchasedMods?.data?.length"
        :fetch="false"
        :loading="loadingPurchases"
        :error="errorPurchases"
        :no-data-text="
          $t('marketplace.no_purchases', {
            ugc: ugcName(null, false, true).value,
          })
        "
      />
    </div>
  </div>

  <base-modal
    v-if="Settings.allowFiltering && filterMenuOpen"
    show
    no-margin
    no-padding
    bottom
    has-close-btn
    :close-on-escape="false"
    class="sm:tw-hidden"
    @close:modal="toggleFilterMenu"
  >
    <filter-menu
      :id="TABLE_ID"
      show
      modal
      :input-loading="!game?.tag_options"
      :tag-options="game?.tag_options"
      :sort-options="LIBRARY_MODS_SORT_OPTIONS"
      @update="updateTable"
    />
  </base-modal>
</template>

<script>
import { subscriptionStore, purchasesStore, gameStore } from '@stores'
import { onMounted, computed, provide, watch, ref } from 'vue'
import { LIBRARY_MODS_SORT_OPTIONS } from '@config/sort.js'
import { schema } from '@config/schemas/mySubscriptions.js'
import ModioTable from '@components/Table/ModioTable.vue'
import FilterMenu from '@components/List/FilterMenu.vue'
import NoResult from '@components/List/NoResult.vue'
import { useBreakpoints } from '@vueuse/core'
import { Settings } from '@config/game.js'
import { BREAKPOINTS } from '@config'
import { useI18n } from 'vue-i18n'
import useFilter, {
  normalizeModFilters,
  toggleFilterMenu,
  filterMenuOpen,
} from '@composables/useFilter.js'
import {
  useResourceValue,
  useSearch,
  useAsync,
  useRoute,
  addToast,
  useSort,
} from '@composables'
import {
  unsubscribeToModRequest,
  getMyPurchasesRequest,
  subscribeToModRequest,
} from '@services'

const TABLE_ID = 'purchased-mods-table'

export default {
  components: {
    ModioTable,
    FilterMenu,
    NoResult,
  },
  setup() {
    const purchasedModsTable = ref(undefined)

    const { setMyPurchases, purchases } = purchasesStore()
    const { getPublicGame } = gameStore()
    const { subscriptions, addSubscription, removeSubscription } =
      subscriptionStore()

    const { setSort } = useSort(TABLE_ID, undefined, LIBRARY_MODS_SORT_OPTIONS)
    const { greaterOrEqual } = useBreakpoints(BREAKPOINTS)
    const { ugcName } = useResourceValue()
    const { routeQuery } = useRoute()
    const { t } = useI18n()
    const {
      clearUserSearch,
      searchUserName,
      inMemorySearch,
      clearSearch,
      search,
    } = useSearch(TABLE_ID)

    const defaultSort = LIBRARY_MODS_SORT_OPTIONS.find((x) => x.default)
    const game = getPublicGame()
    const smAndUp = greaterOrEqual('sm')

    const {
      activeFiltersCount,
      inMemoryFilter,
      resetFilter,
      setFilter,
      filter,
    } = useFilter(TABLE_ID)

    setSort({
      sortBy: defaultSort.key,
      order: defaultSort.direction,
      direction: defaultSort.direction,
    })

    const hasAnyFilters = computed(
      () =>
        !!activeFiltersCount.value || !!search.value || !!searchUserName.value
    )

    watch(smAndUp, (_smAndUp) => {
      if (Settings.value.allowFiltering && _smAndUp !== filterMenuOpen.value) {
        toggleFilterMenu()
      }
    })

    const {
      loading: loadingPurchases,
      error: errorPurchases,
      data: dataPurchases,
      run: runPurchases,
    } = useAsync(getMyPurchasesRequest)

    const {
      run: runResub,
      error: errorResub,
      errorRef: errorRefResub,
    } = useAsync((update) => subscribeToModRequest(update))

    const {
      run: runUnsub,
      error: errorUnsub,
      errorRef: errorRefUnsub,
    } = useAsync((update) => unsubscribeToModRequest(update))

    const purchasedMods = computed(() => {
      let data = purchases.value
      data = inMemorySearch(data)
      data = inMemoryFilter(data)

      return {
        data: data?.map((p) => ({
          ...p,
          unsubscribed: subscriptions.value?.every((s) => s.id !== p.id),
        })),
      }
    })

    onMounted(async () => {
      setupFilters()
      if (
        Settings.value.allowFiltering &&
        !smAndUp.value &&
        filterMenuOpen.value
      ) {
        toggleFilterMenu()
      }

      await runPurchases()

      if (!errorPurchases.value) {
        setMyPurchases(dataPurchases.value.data)
      }
    })

    function resetAllFilters() {
      resetFilter()
      clearSearch()
      clearUserSearch()
    }

    function setupFilters() {
      const queryTags = [routeQuery.value.tags].filter((tag) => tag).join(',')

      if (!filter.value?.length || queryTags.length) {
        setFilter(
          normalizeModFilters(game.value.tag_options, {
            tags: queryTags,
          }),
          !queryTags
        )
      }
    }

    function updateTable(_type, option) {
      if (_type === 'sort') {
        setSort({
          sortBy: option.key,
          order: option.direction,
          direction: option.direction,
        })

        purchasedModsTable.value?.updateTable('sort', option)
      }
    }

    async function toggleSubscribe(mod) {
      const reSub = mod.unsubscribed

      mod = { ...mod }
      delete mod.unsubscribed

      reSub ? addSubscription(mod.id, mod) : removeSubscription(mod.id)

      const subToggleParams = {
        gameNameId: mod.game_name_id,
        modNameId: mod.name_id,
        gameId: mod.game_id,
        modId: mod.id,
      }
      await (reSub ? runResub : runUnsub)(subToggleParams)

      if (reSub ? errorResub.value : errorUnsub.value) {
        // Revert action
        reSub ? removeSubscription(mod.id) : addSubscription(mod.id, mod)

        addToast({
          title: reSub ? t('subscribe_fail') : t('unsubscribe_fail'),
          isError: true,
          errorRef: reSub ? errorRefResub.value : errorRefUnsub.value,
          text: reSub ? errorResub.value : errorUnsub.value,
        })
      }
    }
    provide('toggleSubscribe', toggleSubscribe)

    return {
      LIBRARY_MODS_SORT_OPTIONS,
      purchasedModsTable,
      toggleFilterMenu,
      loadingPurchases,
      resetAllFilters,
      errorPurchases,
      filterMenuOpen,
      purchasedMods,
      hasAnyFilters,
      updateTable,
      Settings,
      TABLE_ID,
      ugcName,
      schema,
      search,
      game,
    }
  },
}
</script>
